import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Collapse,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { Pencil, Trash2, LockKeyhole } from "lucide-react";
import { apiService } from "@/services/api.service";
import { FilterSection } from "@/components/common/FilterSection";
import badgePlus from "@/assets/badge-plus.svg";
import styles from "./RolePermissionsManagementPage.module.css";

const { Text } = Typography;
const ROLES_ENDPOINT = "/rbac/roles";
const PERMISSIONS_ENDPOINT = "/rbac/permissions";

interface Permission {
  id: number;
  resource: string;
  action: string;
  module?: string | null;
}

interface RolePermission {
  permissionId: number;
  permission: Permission;
  fields?: string[] | null;
  conditions?: Record<string, unknown> | null;
}

const DEPARTMENT_SCOPE_CONDITION = "{{user.departmentId}}";
const MANAGER_SCOPE_CONDITION = "{{user.id}}";
const MANAGER_REPORT_READ_FIELDS = [
  "id",
  "fullName",
  "position",
  "departmentId",
  "employeeCode",
  "avatar",
  "isActive",
  "isBlocked",
  "startDate",
  "laborType",
];
const DEPARTMENT_MANAGER_READ_FIELDS = [
  "id",
  "fullName",
  "role",
  "position",
  "workplace",
  "departmentId",
  "employeeCode",
  "avatar",
  "isActive",
  "isBlocked",
];
const DEPARTMENT_MANAGER_UPDATE_FIELDS = ["position", "workplace"];
const DEPARTMENT_MANAGER_CREATE_FIELDS = [
  "fullName",
  "email",
  "position",
  "departmentId",
];

interface RoleRecord {
  id: number;
  name: string;
  description?: string | null;
  isSystem: boolean;
  userCount: number;
  rolePermissions: RolePermission[];
  createdAt?: string;
}

interface RoleFormValues {
  name: string;
  description?: string;
}

const roleLabels: Record<string, string> = {
  admin: "Quản trị viên",
  ceo: "Giám đốc điều hành",
  hr: "Nhân sự",
  accounting: "Kế toán",
  staff: "Nhân viên",
};

const CRUD_OPTIONS = [
  {
    key: "create",
    actions: ["create", "check_in"],
  },
  {
    key: "update",
    actions: ["update", "check_out", "review", "approve", "reject", "cancel"],
  },
  {
    key: "delete",
    actions: ["delete"],
  },
  {
    key: "read",
    actions: ["read", "list"],
  },
] as const;

const CRUD_LABELS: Record<string, Record<string, string>> = {
  "Nhân sự": {
    create: "Thêm nhân sự",
    update: "Chỉnh sửa thông tin nhân sự",
    delete: "Xóa nhân sự",
    read: "Xem danh sách nhân sự",
  },
  "Phòng ban": {
    create: "Thêm phòng ban",
    update: "Chỉnh sửa thông tin phòng ban",
    delete: "Xóa phòng ban",
    read: "Xem danh sách phòng ban",
  },
  "Đơn từ": {
    create: "Tạo đơn từ",
    update: "Duyệt / xử lý đơn từ",
    delete: "Xóa đơn từ",
    read: "Xem đơn từ",
  },
  "Chấm công": {
    create: "Ghi nhận chấm công",
    update: "Điều chỉnh chấm công",
    delete: "Xóa bản ghi chấm công",
    read: "Xem chấm công",
  },
  "Tính lương": {
    read: "Xem bảng chấm công tính lương",
  },
  "Hệ thống": {
    create: "Tạo cấu hình hệ thống",
    update: "Cập nhật hệ thống",
    delete: "Xóa dữ liệu hệ thống",
    read: "Xem nhật ký hệ thống",
  },
};

const crudCopy = (group: string, key: string) =>
  CRUD_LABELS[group]?.[key] ??
  `${{ create: "Thêm", update: "Chỉnh sửa", delete: "Xóa", read: "Xem" }[key]} ${group.toLowerCase()}`;

const formatRoleName = (name: string) => {
  const normalizedName = name.normalize("NFC").replace(/[_-]/g, " ").trim();
  const systemLabel = roleLabels[normalizedName.toLowerCase()];
  if (systemLabel) return systemLabel;

  return normalizedName.replace(
    /(^|\s)(\p{L})/gu,
    (_match, separator: string, letter: string) =>
      `${separator}${letter.toLocaleUpperCase("vi-VN")}`,
  );
};

export default function RolePermissionsManagementPage() {
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleRecord | null>(null);
  const [editorPermissionIds, setEditorPermissionIds] = useState<number[]>([]);
  const [departmentScope, setDepartmentScope] = useState(false);
  const [managerScope, setManagerScope] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [form] = Form.useForm<RoleFormValues>();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [roleData, permissionData] = await Promise.all([
        apiService.get<RoleRecord[]>(ROLES_ENDPOINT),
        apiService.get<Permission[]>(PERMISSIONS_ENDPOINT),
      ]);
      setRoles(roleData);
      setPermissions(permissionData);
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : "Không thể tải danh sách vai trò.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const openCreate = () => {
    setEditingRole(null);
    setEditorPermissionIds([]);
    setDepartmentScope(false);
    setManagerScope(false);
    form.resetFields();
    setEditorOpen(true);
  };

  const openEdit = (role: RoleRecord) => {
    setEditingRole(role);
    setEditorPermissionIds(
      role.rolePermissions?.map((item) => item.permissionId) ?? [],
    );
    setDepartmentScope(
      role.rolePermissions?.some(
        ({ permission, conditions }) =>
          ["user", "attendance", "payroll"].includes(permission?.resource ?? "") &&
          conditions?.departmentId === DEPARTMENT_SCOPE_CONDITION,
      ) ?? false,
    );
    setManagerScope(
      role.rolePermissions?.some(
        ({ permission, conditions }) =>
          permission?.resource === "user" &&
          conditions?.managerId === MANAGER_SCOPE_CONDITION,
      ) ?? false,
    );
    form.setFieldsValue({
      name: role.name,
      description: role.description ?? "",
    });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingRole(null);
    setEditorPermissionIds([]);
    setDepartmentScope(false);
    setManagerScope(false);
    form.resetFields();
  };

  const saveRole = async () => {
    let values: RoleFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      let savedRole: RoleRecord;
      let recoveredExistingRole = false;
      if (editingRole) {
        const updateValues = editingRole.isSystem
          ? { description: values.description }
          : values;
        savedRole = await apiService.patch<RoleRecord>(
          `${ROLES_ENDPOINT}/${editingRole.id}`,
          updateValues,
        );
      } else {
        try {
          savedRole = await apiService.post<RoleRecord>(ROLES_ENDPOINT, values);
        } catch (createError) {
          // A previous attempt may have created the role before one of its
          // permission requests failed. Reuse it so retrying completes setup.
          const existingRoles = await apiService.get<RoleRecord[]>(ROLES_ENDPOINT);
          const existingRole = existingRoles.find(
            (role) => role.name.toLowerCase() === values.name.trim().toLowerCase(),
          );
          if (!existingRole) throw createError;
          savedRole = existingRole;
          recoveredExistingRole = true;
        }
      }

      const roleBeforeSave = editingRole ?? (recoveredExistingRole ? savedRole : null);
      const currentIds = new Set(
        roleBeforeSave?.rolePermissions?.map((item) => item.permissionId) ?? [],
      );
      const selectedIds = new Set(editorPermissionIds);
      const addIds = [...selectedIds].filter((id) => !currentIds.has(id));
      const removeIds = [...currentIds].filter((id) => !selectedIds.has(id));
      const existingPermissions = new Map(
        (roleBeforeSave?.rolePermissions ?? []).map((grant) => [
          grant.permissionId,
          grant,
        ]),
      );
      const getScopedGrant = (permission: Permission) => {
        if (
          managerScope &&
          permission.resource === "user" &&
          ["read", "list"].includes(permission.action)
        ) {
          return {
            conditions: { managerId: MANAGER_SCOPE_CONDITION },
            fields: MANAGER_REPORT_READ_FIELDS,
          };
        }
        if (
          departmentScope &&
          ["attendance", "payroll"].includes(permission.resource) &&
          permission.action === "read"
        ) {
          return {
            conditions: { departmentId: DEPARTMENT_SCOPE_CONDITION },
            fields: null,
          };
        }
        if (departmentScope && permission.resource === "user") {
          let fields: string[] | null = null;
          if (permission.action === "read" || permission.action === "list") {
            fields = DEPARTMENT_MANAGER_READ_FIELDS;
          } else if (permission.action === "update") {
            fields = DEPARTMENT_MANAGER_UPDATE_FIELDS;
          } else if (permission.action === "create") {
            fields = DEPARTMENT_MANAGER_CREATE_FIELDS;
          }
          return {
            conditions: { departmentId: DEPARTMENT_SCOPE_CONDITION },
            fields,
          };
        }
        return null;
      };
      const permissionScopeUpdates = [...selectedIds].flatMap((permissionId) => {
        const permission = permissions.find((item) => item.id === permissionId);
        if (
          !permission ||
          !["user", "attendance", "payroll"].includes(permission.resource)
        ) return [];

        const existing = existingPermissions.get(permissionId);
        if (!existing) return [];
        const hadDepartmentScope =
          existing?.conditions?.departmentId === DEPARTMENT_SCOPE_CONDITION;
        const hadManagerScope =
          permission.resource === "user" &&
          existing?.conditions?.managerId === MANAGER_SCOPE_CONDITION;
        const desiredScope = getScopedGrant(permission);
        if (!desiredScope && !hadDepartmentScope && !hadManagerScope) return [];

        const endpoint = `${ROLES_ENDPOINT}/${savedRole.id}/permissions/${permissionId}`;
        if (!desiredScope) {
          return [
            apiService.patch(endpoint, {
              conditions: null,
              fields: existing?.fields ?? null,
            }),
          ];
        }
        // Persist the current field allowlist for scoped user grants.
        return [apiService.patch(endpoint, desiredScope)];
      });
      await Promise.all([
        ...addIds.map((permissionId) => {
          const permission = permissions.find((item) => item.id === permissionId);
          const scopedGrant = permission ? getScopedGrant(permission) : null;
          return apiService.post(
            `${ROLES_ENDPOINT}/${savedRole.id}/permissions/${permissionId}`,
            scopedGrant ?? {},
          );
        }),
        ...removeIds.map((permissionId) =>
          apiService.delete(
            `${ROLES_ENDPOINT}/${savedRole.id}/permissions/${permissionId}`,
          ),
        ),
        ...permissionScopeUpdates,
      ]);

      message.success(
        editingRole
          ? "Đã cập nhật vai trò và quyền."
          : recoveredExistingRole
            ? "Đã hoàn tất vai trò và quyền."
            : "Đã tạo vai trò mới.",
      );
      closeEditor();
      await loadData();
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : "Không thể lưu vai trò.",
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async (role: RoleRecord) => {
    try {
      await apiService.delete(`${ROLES_ENDPOINT}/${role.id}`);
      message.success("Đã xóa vai trò.");
      setSelectedRowKeys((keys) => keys.filter((key) => key !== role.id));
      await loadData();
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : "Không thể xóa vai trò.",
      );
    }
  };

  const columns = useMemo<ColumnsType<RoleRecord>>(
    () => [
      {
        title: "Vai trò",
        dataIndex: "name",
        key: "name",
        width: "21%",
        render: (name: string, role) => (
          <div className={styles.roleNameCell}>
            {role.isSystem && (
              <span className={styles.roleMark}>
                <LockKeyhole size={15} />
              </span>
            )}
            <span className={styles.roleName}>{formatRoleName(name)}</span>
            {role.isSystem && <Tag className={styles.systemTag}>Hệ thống</Tag>}
          </div>
        ),
      },
      {
        title: "Quyền được cấp",
        key: "permissions",
        width: "19%",
        render: (_, role) => (
          <Button
            type="link"
            className={styles.countLink}
            onClick={() => openEdit(role)}
          >
            {
              new Set(
                role.rolePermissions?.map(
                  (item) =>
                    item.permission?.module || item.permission?.resource,
                ),
              ).size
            }{" "}
            nhóm
          </Button>
        ),
      },
      {
        title: "Số người dùng",
        dataIndex: "userCount",
        key: "userCount",
        width: "16%",
        render: (count: number) => (
          <Text className={styles.mutedText}>{count} người</Text>
        ),
      },
      {
        title: "Mô tả",
        key: "description",
        width: "25%",
        render: (_, role) => (
          <Text className={styles.description}>{role.description || "—"}</Text>
        ),
      },
      {
        title: "Ngày tạo",
        dataIndex: "createdAt",
        key: "createdAt",
        width: "12%",
        render: (createdAt?: string) => (
          <Text className={styles.mutedText}>
            {createdAt ? new Date(createdAt).toLocaleDateString("vi-VN") : "—"}
          </Text>
        ),
      },
      {
        title: "Hành động",
        key: "actions",
        width: 112,
        align: "center",
        render: (_, role) => (
          <Space size={4}>
            <Tooltip
              title={
                role.isSystem
                  ? "Sửa mô tả và quyền hệ thống"
                  : "Sửa vai trò và quyền"
              }
            >
              <Button
                type="text"
                aria-label={`Sửa ${formatRoleName(role.name)}`}
                icon={<Pencil size={16} />}
                className={styles.editAction}
                onClick={() => openEdit(role)}
              />
            </Tooltip>
            <Tooltip
              title={
                role.isSystem ? "Không thể xóa role hệ thống" : "Xóa vai trò"
              }
            >
              <span>
                <Popconfirm
                  title="Xóa vai trò này?"
                  description="Chỉ có thể xóa vai trò chưa được gán cho người dùng."
                  okText="Xóa"
                  cancelText="Hủy"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => deleteRole(role)}
                  disabled={role.isSystem}
                >
                  <Button
                    type="text"
                    danger
                    aria-label={`Xóa ${formatRoleName(role.name)}`}
                    icon={<Trash2 size={16} />}
                    className={styles.deleteAction}
                    disabled={role.isSystem}
                  />
                </Popconfirm>
              </span>
            </Tooltip>
          </Space>
        ),
      },
    ],
    [],
  );

  const permissionGroups = useMemo(() => {
    const grouped = new Map<string, Permission[]>();
    for (const permission of permissions) {
      // Older test databases may contain a mojibaked module name. Payroll has
      // a stable resource key, so always render its canonical Vietnamese label.
      const group =
        permission.resource === "payroll"
          ? "Tính lương"
          : permission.module || permission.resource;
      grouped.set(group, [...(grouped.get(group) ?? []), permission]);
    }
    return [...grouped.entries()];
  }, [permissions]);

  const filteredRoles = useMemo(() => {
    const query = searchText.trim().toLocaleLowerCase("vi");
    if (!query) return roles;
    return roles.filter((role) =>
      [formatRoleName(role.name), role.name, role.description ?? ""]
        .join(" ")
        .toLocaleLowerCase("vi")
        .includes(query),
    );
  }, [roles, searchText]);

  const handleClearFilter = () => setSearchText("");

  return (
    <main className={styles.page}>
      <FilterSection
        title="Bộ lọc"
        subtitle="Tìm kiếm và lọc vai trò, quyền hạn"
        searchPlaceholder="Tìm kiếm theo tên vai trò..."
        searchValue={searchText}
        onSearchChange={setSearchText}
        onClearFilter={handleClearFilter}
        isFilterActive={Boolean(searchText.trim())}
        showDepartmentFilter={false}
        extraActions={
          <Button
            type="primary"
            icon={<img src={badgePlus} alt="add" className={styles.addIcon} />}
            size="large"
            onClick={openCreate}
            className={styles.addButton}
          >
            Thêm vai trò
          </Button>
        }
      />

      <section className={styles.tableCard}>
        {selectedRowKeys.length > 0 && (
          <div className={styles.selectionBar}>
            Đã chọn <strong>{selectedRowKeys.length}</strong> vai trò
            <Button type="link" onClick={() => setSelectedRowKeys([])}>
              Bỏ chọn
            </Button>
          </div>
        )}
        <Table<RoleRecord>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={filteredRoles}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            columnWidth: 48,
          }}
          pagination={{
            pageSize: 8,
            showSizeChanger: false,
            hideOnSinglePage: true,
          }}
          className={styles.rolesTable}
          locale={{ emptyText: <Empty description="Chưa có vai trò nào" /> }}
          scroll={{ x: 780 }}
        />
      </section>

      <Modal
        title={
          editingRole ? "Chỉnh sửa vai trò & quyền" : "Thêm vai trò & quyền"
        }
        className={styles.roleEditorModal}
        open={editorOpen}
        onCancel={closeEditor}
        onOk={() => void saveRole()}
        okText={editingRole ? "Cập nhật" : "Thêm"}
        cancelText="Hủy"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          className={styles.roleForm}
        >
          <Form.Item
            name="name"
            label="Tên vai trò"
            rules={[
              { required: true, message: "Vui lòng nhập tên vai trò." },
              {
                min: 2,
                max: 50,
                message: "Tên vai trò cần từ 2 đến 50 ký tự.",
              },
            ]}
          >
            <Input
              placeholder="Nhập tên vai trò"
              maxLength={50}
              disabled={Boolean(editingRole?.isSystem)}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="Mô tả"
            className={styles.descriptionField}
          >
            <Input.TextArea
              placeholder="Nhập mô tả vai trò"
              maxLength={255}
              autoSize={{ minRows: 2, maxRows: 3 }}
              showCount
              className={styles.roleForm}
            />
          </Form.Item>
          <div className={styles.permissionEditor}>
            {permissions.some((permission) =>
              ["user", "attendance", "payroll"].includes(permission.resource),
            ) && (
              <div className={styles.departmentScopeOption}>
                <Checkbox
                  checked={departmentScope}
                  onChange={(event) => setDepartmentScope(event.target.checked)}
                >
                  Giới hạn quyền nhân sự trong cùng phòng ban
                </Checkbox>
                <Text type="secondary">
                  Quyền xem nhân sự, chấm công và tính lương đã chọn chỉ hiển thị dữ liệu của nhân viên cùng phòng ban với người giữ vai trò này.
                </Text>
                <Checkbox
                  checked={managerScope}
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    setManagerScope(enabled);
                    const managerReadIds = permissions
                      .filter(
                        (permission) =>
                          permission.resource === "user" &&
                          ["read", "list"].includes(permission.action),
                      )
                      .map((permission) => permission.id);
                    if (!enabled) {
                      if (departmentScope) return;
                      setEditorPermissionIds((current) =>
                        current.filter((id) => !managerReadIds.includes(id)),
                      );
                      return;
                    }
                    setEditorPermissionIds((current) =>
                      [...new Set([...current, ...managerReadIds])],
                    );
                  }}
                >
                  Chỉ xem nhân viên được giao quản lý trực tiếp
                </Checkbox>
                <Text type="secondary">
                  Người giữ vai trò chỉ xem danh sách và thông tin cơ bản của nhân viên được phân công trực tiếp cho họ. Hãy gán vai trò này cho người quản lý và chọn đúng người quản lý trong hồ sơ nhân viên. Phòng ban, chức danh không tự cấp quyền.
                </Text>
              </div>
            )}
            {permissionGroups.length ? (
              <Collapse
                bordered={false}
                defaultActiveKey={permissionGroups.map(([group]) => group)}
                items={permissionGroups.map(([group, groupPermissions]) => {
                  const crudPermissionIds = groupPermissions
                    .filter((permission) =>
                      CRUD_OPTIONS.some((option) =>
                        (option.actions as readonly string[]).includes(
                          permission.action,
                        ),
                      ),
                    )
                    .map((permission) => permission.id);
                  const allCrudChecked =
                    crudPermissionIds.length > 0 &&
                    crudPermissionIds.every((id) =>
                      editorPermissionIds.includes(id),
                    );
                  const someCrudChecked = crudPermissionIds.some((id) =>
                    editorPermissionIds.includes(id),
                  );

                  return {
                    key: group,
                    label: (
                      <div className={styles.permissionGroupHeader}>
                        <span className={styles.permissionGroupTitle}>
                          {group}
                        </span>
                        <Checkbox
                          checked={allCrudChecked}
                          indeterminate={someCrudChecked && !allCrudChecked}
                          disabled={crudPermissionIds.length === 0}
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) => {
                            setEditorPermissionIds((current) => {
                              const next = new Set(current);
                              crudPermissionIds.forEach((id) =>
                                event.target.checked
                                  ? next.add(id)
                                  : next.delete(id),
                              );
                              return [...next];
                            });
                          }}
                        ></Checkbox>
                      </div>
                    ),
                    children: (
                      <div className={styles.permissionOptions}>
                        {CRUD_OPTIONS.map((option) => {
                          const label = crudCopy(group, option.key);
                          const ids = groupPermissions
                            .filter((permission) =>
                              (option.actions as readonly string[]).includes(
                                permission.action,
                              ),
                            )
                            .map((permission) => permission.id);
                          const checked =
                            ids.length > 0 &&
                            ids.every((id) => editorPermissionIds.includes(id));
                          const indeterminate =
                            ids.some((id) =>
                              editorPermissionIds.includes(id),
                            ) && !checked;

                          return (
                            <div
                              className={styles.permissionRow}
                              key={`${group}-${option.key}`}
                            >
                              <Checkbox
                                checked={checked}
                                indeterminate={indeterminate}
                                disabled={ids.length === 0}
                                onChange={(event) => {
                                  setEditorPermissionIds((current) => {
                                    const next = new Set(current);
                                    ids.forEach((id) =>
                                      event.target.checked
                                        ? next.add(id)
                                        : next.delete(id),
                                    );
                                    return [...next];
                                  });
                                }}
                              >
                                {label}
                              </Checkbox>
                            </div>
                          );
                        })}
                      </div>
                    ),
                  };
                })}
              />
            ) : (
              <Empty description="Chưa có quyền nào trong hệ thống" />
            )}
          </div>
        </Form>
      </Modal>
    </main>
  );
}
